import {useTranslation} from 'react-i18next';
import {Radio} from '@mui/material';
import AppTextInput from '../../../../components/common/AppTextInput';
import {
  FormQuestion,
  FormQuestionOption,
} from '../../../../services/operations/forms';
import FilePreview from '../../../forms/components/AddEditForm/components/QuestionsTab/FilePreview';

const OTHER_MAX_LENGTH = 100;

/** Answer shape for a multiple-choice question */
export interface MultipleChoiceAnswer {
  selected: string; // option id or 'other'
  otherText?: string;
}

type Props = {
  question: FormQuestion;
  value: MultipleChoiceAnswer | null;
  onChange: (value: MultipleChoiceAnswer | null) => void;
};

function PublicMultipleChoice({question, value, onChange}: Props) {
  const {t} = useTranslation();

  const selectedId = value?.selected ?? '';

  const handleSelect = (optionId: string) => {
    onChange({selected: optionId, otherText: value?.otherText});
  };

  const handleOtherTextChange = (text: string) => {
    if (text.length > OTHER_MAX_LENGTH) return;
    // Auto-select "other" when user starts typing
    onChange({selected: 'other', otherText: text});
  };

  const handleClear = () => {
    onChange(null);
  };

  const otherOption = question?.options?.find(op => op?.isOther);

  return (
    <div className="flex flex-col gap-3 mt-3">
      <div className="flex flex-col gap-4">
        {/* Regular options */}
        {question?.options.map((option: FormQuestionOption) => {
          if (option.isOther) return null;
          return (
            <div key={option._id} className="flex flex-col">
              <div
                className="flex items-center gap-3 cursor-pointer"
                onClick={() => handleSelect(option._id!)}>
                {/* Radio circle */}
                <Radio
                  color="secondary"
                  style={{padding: 0}}
                  checked={selectedId === option._id}
                  value={selectedId}
                  onChange={() => handleSelect(option._id!)}
                />

                {/* Option label */}
                <span className="text-[16px] text-textColor">
                  {option?.label}
                </span>
              </div>

              {/* Option file (below the option row) */}
              {option?.image && (
                <div className="mt-2 flex justify-center">
                  <FilePreview file={option.image} />
                </div>
              )}
            </div>
          );
        })}

        {/* "Other" option — radio + full-width text input on the same row */}
        {otherOption && (
          <div className="flex flex-col">
            <div className="flex items-center gap-3">
              {/* Radio circle */}
              <div
                className="flex-shrink-0 cursor-pointer"
                onClick={() =>
                  onChange({
                    selected: 'other',
                    otherText: value?.otherText ?? '',
                  })
                }>
                <Radio
                  color="secondary"
                  style={{padding: 0}}
                  checked={selectedId === 'other'}
                  value={selectedId}
                  onChange={() =>
                    onChange({
                      selected: 'other',
                      otherText: value?.otherText ?? '',
                    })
                  }
                />
              </div>

              {/* Full-width text input */}
              <AppTextInput
                maxLength={OTHER_MAX_LENGTH}
                value={value?.otherText ?? ''}
                onChange={e => handleOtherTextChange(e.target.value)}
                placeholder={t('forms.dialog.other')}
                className="border-none"
              />
            </div>

            {/* Other option file */}
            {otherOption?.image && (
              <div className="mt-2 flex justify-center">
                <FilePreview file={otherOption.image} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Clear selection link */}
      <div className="flex">
        <button
          type="button"
          onClick={handleClear}
          className="text-linkBlue underline hover:opacity-80 transition-opacity">
          {t('forms.public.clearSelection')}
        </button>
      </div>
    </div>
  );
}

export default PublicMultipleChoice;
